import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Platform,
} from "react-native";
import { ReactCountryFlag } from "react-country-flag";
import Colors from "../../../../constants/colors";
import GradientBorderBox from "../../../uiComponents/GradiendBorderBox";
import DownArrow from "../../../../../../../assets/images/DownArrowIcon.svg";
import BackIcon from "../../../../../../../assets/images/BackIcon.svg";
import CloseIcon from "../../../../../../../assets/images/CloseIcon.svg";
import RadioSelectedIcon from "../../../../../../../assets/images/RadioSelectButton.svg";
import { useJourneyStore } from "../store/sendmoneyJourneyStore";
import { useWorkflowNavigation } from "../hooks/useWorkflowNavigation";
import { useStepAction } from "../hooks/useStepAction";
import { sendMoneyService } from "../../../../services/sendmoney.service";
import { useApiWithLoading } from "../../../../hooks/useApiWithLoading";
import type { QuoteRequest } from "../models/quoteRequest";
import type { QuoteResponse } from "../models/quoteResponse";
import { useSessionStore } from "../../../../../../state/src/useSessionStore";
import {
  buildMapiCurrencyOptionsFromTransactions,
  getFxFromQuoteData,
  getMapiQuoteTransactions,
} from "../../../../helper/mapiQuoteResponseHelpers";

type CurrencyOption = {
  currency: string;
  rate: number;
};

const parseAmount = (text: string): string => {
  const cleaned = text.replace(/[^0-9.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    return `${parts[0]}.${parts.slice(1).join("")}`;
  }
  return cleaned;
};

const computeReceiveFromSend = (sendStr: string | undefined, rate: number) => {
  const s = parseFloat(sendStr || "0");
  if (!isFinite(s)) return "0.00";
  return (s * (rate || 0)).toFixed(2);
};

const computeSendFromReceive = (recvStr: string, rate: number) => {
  const r = parseFloat(recvStr || "0");
  if (!isFinite(r) || !rate) return "0.00";
  return (r / rate).toFixed(2);
};

export function AmountEntryScreen() {
  const { t } = useTranslation();
  const destinationCountry = useJourneyStore(
    (state) => state.journeyData.destinationCountry
  );
  const destinationState = useJourneyStore(
    (state) => state.journeyData.destinationState
  );
  const updateJourneyData = useJourneyStore((state) => state.updateJourneyData);
  const { goNext } = useWorkflowNavigation();
  const { executeWithLoading } = useApiWithLoading();

  const [sendAmount, setSendAmount] = useState("");
  const [receiveAmount, setReceiveAmount] = useState("");
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [localRate, setLocalRate] = useState(1);
  const [localCurrency, setLocalCurrency] = useState("");
  const [quoteData, setQuoteData] = useState<QuoteResponse | null>(null);

  const alpha2 = destinationCountry?.iso2Code?.toUpperCase() || "";

  const currencyOptions: CurrencyOption[] = useMemo(
    () =>
      buildMapiCurrencyOptionsFromTransactions(
        getMapiQuoteTransactions(quoteData)
      ),
    [quoteData]
  );

  const sendValue = parseFloat(sendAmount || "0");
  const isValid = sendAmount !== "0.00" && sendAmount !== "" && sendValue > 0;
  const showConversion = isValid && localRate > 0 && localCurrency !== "";

  useEffect(() => {
    if (sendAmount && localRate > 0) {
      setReceiveAmount(computeReceiveFromSend(sendAmount, localRate));
    }
  }, [localRate]);

  const handleCurrencySelect = (opt: CurrencyOption) => {
    setSelectedCurrency(opt.currency);
    setLocalCurrency(opt.currency);
    setLocalRate(opt.rate);
    setReceiveAmount(computeReceiveFromSend(sendAmount, opt.rate));
    setShowCurrencyModal(false);
  };

  const handleNext = async () => {
    if (!isValid) {
      return;
    }

   
    const quoteRequest = frameQuoteRequest(sendValue, destinationCountry?.sendCurrencies[0] ?? "USD",);
    const quote = await executeWithLoading(
      () => sendMoneyService.getQuote(quoteRequest),
      "Processing..."
    );
    updateJourneyData({
        sendAmount: sendValue,
        receiveAmount: parseFloat(receiveAmount || "0"),
        sendCurrency: destinationCountry?.sendCurrencies[0] ?? "USD",
        receiveCurrency: localCurrency,
        quoteResponse: quote,
      });
    goNext();
  };

  const frameQuoteRequest = (sendAmount: number, sendCurrencyCode: string, selectedServiceOption?: string) => {
    const quoteRequest: QuoteRequest = {
      targetAudience: "CONSUMER_FACING",
      agentPartnerId: Number(useSessionStore.getState().agentId),
      destinationCountryCode: destinationCountry?.iso3Code ?? "",
      ...(destinationState?.code
        ? { destinationCountrySubdivisionCode: destinationState.code }
        : {}),
    // dont include selectedServiceOption in the request if it is not provided
      serviceOptionCode: selectedServiceOption ?? undefined,
      sendAmount: {
        value: sendAmount,
        currencyCode: sendCurrencyCode
      },
      fundInStore: {
        fundInStore: true,
      },
    };
    return quoteRequest;
  }

  useEffect(() => {
    const fetchInitialQuote = async () => {
      const agentId = useSessionStore.getState().agentId;
      if (!agentId || !destinationCountry?.iso3Code) return;
      // Default to first send currency for the destination country; revisit when user can pick currency.
      const sendCurrencyCode =
        destinationCountry.sendCurrencies[0] ?? "USD";
    // we are passing WILL_CALL that will be used to get the fx rate from the quote response.
    // if we dont include it, then Quore Response will include transactions for all service options.
      const quoteRequest = frameQuoteRequest(1, sendCurrencyCode, "WILL_CALL");
      try {
        setIsQuoteLoading(true);
        const quote = await sendMoneyService.getQuote(quoteRequest);
        setQuoteData(quote);
        const { rate, receiveCurrency } = getFxFromQuoteData(quote);
        setLocalRate(rate);
        setLocalCurrency(receiveCurrency);
        setSelectedCurrency(receiveCurrency || null);
        console.log("[SendMoney] initial quote", quote);
      } catch (e) {
        console.error("Failed to load initial quote in AmountEntryScreen", e);
      } finally {
        setIsQuoteLoading(false);
      }
    };

    fetchInitialQuote();
  }, [destinationCountry?.iso3Code, destinationState?.code]);

  useStepAction(handleNext);

  return (
    <View style={styles.container} testID="amount-entry-screen">
      {showConversion && (
        <Text style={styles.title}>
          1 USD = {localRate.toFixed(4)} {localCurrency}
        </Text>
      )}
      <Text style={styles.youSendTitle}>{t("YOU_SEND")}</Text>
      <View
        style={[
          styles.amountInput,
          isQuoteLoading ? { opacity: 0.5 } : undefined,
        ]}
      >
        <View style={styles.amountRow}>
          <View style={styles.shrinkWrap}>
            <TextInput
              testID="sendMoneySendAmount"
              autoFocus
              style={styles.amountText}
              keyboardType="decimal-pad"
              placeholder="0.00"
              placeholderTextColor={Colors.subTitleColor}
              editable={!isQuoteLoading}
              value={sendAmount}
              onChangeText={(text) => {
                const next = parseAmount(text);
                setSendAmount(next);
                setReceiveAmount(computeReceiveFromSend(next, localRate));
              }}
            />
          </View>
          <View style={styles.currencyColumn}>
            {Platform.OS === "web" ? (
              <View style={styles.flagOverflow}>
                <ReactCountryFlag
                  countryCode="US"
                  svg
                  style={{ width: 24, height: 24, borderRadius: 50 }}
                  aria-label="USD"
                />
              </View>
            ) : (
              <Text style={{ fontSize: 16 }}>🇺🇸</Text>
            )}
            <Text style={styles.currencyText}>USD</Text>
          </View>
        </View>
      </View>

      <Text style={[styles.youSendTitle, { marginTop: 24 }]}>
        {t("THEY_RECEIVE")}
      </Text>
      <View
        style={[
          styles.amountInput,
          { marginBottom: 48 },
          isQuoteLoading ? { opacity: 0.5 } : undefined,
        ]}
      >
        <TextInput
          testID="sendMoneyReceiveAmount"
          style={styles.amountText}
          keyboardType="decimal-pad"
          placeholder="0.00"
          placeholderTextColor={Colors.subTitleColor}
          editable={!isQuoteLoading}
          value={receiveAmount}
          onChangeText={(text) => {
            const next = parseAmount(text);
            setReceiveAmount(next);
            setSendAmount(computeSendFromReceive(next, localRate));
          }}
        />
        <TouchableOpacity
          disabled={currencyOptions.length <= 1 || isQuoteLoading}
          onPress={() => setShowCurrencyModal(true)}
          testID="sendMoneyReceiveCurrencyButton"
        >
          <View style={styles.currencyColumn}>
            {Platform.OS === "web" && alpha2 ? (
              <View style={styles.flagOverflow}>
                <ReactCountryFlag
                  countryCode={alpha2}
                  svg
                  style={{ width: 24, height: 24, borderRadius: 50 }}
                  aria-label={localCurrency || ""}
                />
              </View>
            ) : (
              <Text style={{ fontSize: 16 }}>🌍</Text>
            )}
            <View style={styles.currencyLabelRow}>
              <Text style={styles.currencyText}>{localCurrency || "—"}</Text>
              {currencyOptions.length > 1 && (
                <DownArrow width={16} height={16} />
              )}
            </View>
          </View>
        </TouchableOpacity>
      </View>
      <GradientBorderBox>
        <Text style={styles.subtitle}>{t("AMOUNT_ENTRY_SUBTITLE")}</Text>
      </GradientBorderBox>

      <Modal visible={showCurrencyModal} animationType="slide" transparent>
        <View style={styles.modalOverlay}>
          <View style={styles.bottomSheet}>
            <View style={styles.modalHeaderRow}>
              <TouchableOpacity
                onPress={() => setShowCurrencyModal(false)}
                testID="sendMoneyCurrencyModalBack"
              >
                <BackIcon width={24} height={24} />
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setShowCurrencyModal(false)}
                testID="sendMoneyCurrencyModalClose"
              >
                <CloseIcon width={24} height={24} />
              </TouchableOpacity>
            </View>
            <Text style={styles.chooseCurrency}>
              {t("CHOOSE_CURRENCY_YOUR_RECEIVER_GETS")}
            </Text>
            {currencyOptions.map((opt) => (
              <TouchableOpacity
                key={opt.currency}
                style={styles.currencyModalOptionRow}
                onPress={() => handleCurrencySelect(opt)}
              >
                <Text style={styles.currencyOptionText}>{opt.currency}</Text>
                <View
                  style={[
                    styles.radioOuter,
                    selectedCurrency === opt.currency && styles.radioOuterSelected,
                  ]}
                >
                  {selectedCurrency === opt.currency && (
                    <RadioSelectedIcon width={20} height={20} />
                  )}
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: 16,
    paddingTop: 32,
  },
  title: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
    letterSpacing: 0.07,
  },
  youSendTitle: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 22,
    marginBottom: 6,
    marginTop: 48,
  },
  flagOverflow: {
    width: 16,
    height: 16,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  amountInput: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 6,
  },
  amountRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  shrinkWrap: {
    flexShrink: 1,
    flexGrow: 0,
    alignSelf: "center",
  },
  amountText: {
    fontFamily: "Rubik",
    fontSize: 40,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 46,
    textAlign: "center",
    minWidth: 60,
    maxWidth: 200,
    backgroundColor: "transparent",
    paddingVertical: 0,
    paddingHorizontal: 0,
    textAlignVertical: "center",
    includeFontPadding: false,
    ...({ outlineWidth: 0 } as object),
  },
  currencyColumn: {
    marginLeft: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  currencyLabelRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  currencyText: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "500",
    color: Colors.subTitleColor,
    lineHeight: 18,
  },
  subtitle: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
    letterSpacing: 0.07,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: "flex-end",
    backgroundColor: "rgba(0,0,0,0.4)",
  },
  bottomSheet: {
    backgroundColor: Colors.white,
    padding: 24,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  },
  modalHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  chooseCurrency: {
    fontFamily: "Rubik",
    fontSize: 24,
    fontWeight: "500",
    color: Colors.modalTitle,
    lineHeight: 30,
    paddingRight: 50,
    marginBottom: 24,
  },
  currencyModalOptionRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 32,
  },
  currencyOptionText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "400",
    color: Colors.darkGrey,
    lineHeight: 24,
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#D1D1D1",
  },
  radioOuterSelected: {
    borderColor: "#000",
  },
});
